import React, { useState } from 'react';
import { CheckCircle2, Shield, Search, Info, Wrench } from 'lucide-react';
import { Button } from '@shared/ui/button';
import { useForm } from 'react-hook-form';
import { DynamicForm } from '@shared/components/DynamicForm';
import { FIELD_CLASSES } from '@shared/utils/constants';
import { useUtilityRequestConfiguration } from '../../../../hooks';
import { getRemoteUtilityId } from '@shared/utils/loginUtils';
import { useTranslation } from 'react-i18next';
import { useToast } from '@shared/hooks/use-toast';

interface Props {
  onNext?: () => void;
  onPrevious?: () => void;
  allStepData?: any;
  onDataChange?: (data: any) => void;
  data?: any;
}

export const ServiceRequest: React.FC<Props> = ({ onNext, onPrevious, allStepData, data, onDataChange }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const accountData = allStepData?.[0]?.accountData;

  const { data: configData, isFetching } = useUtilityRequestConfiguration({
    remote_utility_id: accountData?.remoteUtilityId?.toString() || getRemoteUtilityId(),
    request_type: 'Service',
    disable_pagination: true,
  });

  const services = React.useMemo(() => {
    if (!configData?.result) return [];
    return configData.result.map((item: any) => ({
      id: item.id?.toString(),
      title: item.name || '',
      desc: item.longDescription || item.long_description || '',
      price: item.extraData?.serviceCharge || item.extra_data?.service_charge || 0,
      category: item.supportRequestTypeDisplay || item.support_request_type_display || 'GENERAL'
    }));
  }, [configData]);

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedService, setSelectedService] = useState(data?.serviceType || '');
  
  const form = useForm({
    defaultValues: {
      preferredDate: data?.preferredDate || '',
      preferredTimeSlot: data?.preferredTimeSlot || '',
      additionalInstructions: data?.description || ''
    }
  });

  const fields: any[] = [
    { name: 'preferredDate', label: t("Preferred Date"), type: 'datepicker' },
    { 
      name: 'preferredTimeSlot', 
      label: t("Preferred Time Slot"), 
      type: 'select', 
      options: [
        { label: t("Morning (8 AM - 12 PM)"), value: 'morning' },
        { label: t("Afternoon (12 PM - 4 PM)"), value: 'afternoon' },
        { label: t("Evening (4 PM - 8 PM)"), value: 'evening' }
      ],
      placeholder: t("Select Preferred Time Slot")
    },
    { 
      name: 'additionalInstructions', 
      label: t("Additional Instructions (Optional)"), 
      type: 'textarea', 
      placeholder: t("Include any special requirements, access instructions, or specific requests"),
      className:"col-span-2"
    }
  ];

  const handleSubmit = (formData: any) => {
    if (!selectedService) {
      toast({
        title: "Validation Error",
        description: "Please select a service type.",
        variant: "destructive"
      });
      return;
    }

    const selectedServiceObj = services.find((s: any) => s.id === selectedService);
    onDataChange?.({
      ...data,
      serviceType: selectedService,
      serviceTitle: selectedServiceObj?.title,
      description: formData.additionalInstructions,
      preferredDate: formData.preferredDate,
      preferredTimeSlot: formData.preferredTimeSlot,
      requestTime: new Date().toLocaleString()
    });
    onNext?.();
  };

  const getSelectedServiceTitle = () => {
    if (!selectedService) return t("Not selected");
    return services.find((s: any) => s.id === selectedService)?.title || t("Not selected");
  };

  const filteredServices = services.filter((s: any) => 
    s.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
    s.desc.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      {/* Left Column */}
      <div className="flex-1 space-y-6">
        
        {/* Account Banner */}
        <div className="bg-white rounded-2xl p-4 border border-green-200 shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full border border-green-500 flex items-center justify-center text-green-500 shrink-0 bg-green-50">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{`${accountData?.firstName || ''} ${accountData?.lastName || ''}`.trim()}</p>
              <p className="text-xs text-gray-500">{accountData?.consumerNo} · {accountData?.ownershipDisplay}</p>
            </div>
          </div>
          <button 
            type="button" 
            onClick={onPrevious} 
            className="text-sm font-semibold text-green-600 hover:text-green-700 underline"
          >
            {t("Change")}
          </button>
        </div>

        {/* Form Area */}
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                {t("Select Service")} 
                <Info className="w-4 h-4 text-gray-400" />
              </h3>
              <p className="text-sm text-gray-500">{t("Browse all available services and select the one you need")}</p>
            </div>

            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400"/>
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder={t("Search services...")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
                {t("Available Services")}
                <Info className="w-4 h-4 text-gray-400" />
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[400px] overflow-y-auto pr-2 pb-2">
                {isFetching ? (
                  <div className="col-span-full py-8 text-center text-gray-500">
                    {t("Loading services...")}
                  </div>
                ) : filteredServices.length === 0 ? (
                  <div className="col-span-full py-8 text-center text-gray-500">
                    {t("No services found matching your search.")}
                  </div>
                ) : (
                  filteredServices.map((service: any) => {
                    const isSelected = selectedService === service.id;
                    return (
                      <div 
                        key={service.id} 
                        className={`flex flex-col border rounded-xl p-5 transition-all ${
                          isSelected ? 'border-blue-500 ring-1 ring-blue-500 shadow-sm bg-blue-50/10' : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <h5 className="font-semibold text-gray-900 pr-2 leading-tight">{service.title}</h5>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium tracking-wide bg-purple-600 text-white`}>
                            {service.category}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-500 flex-grow mb-4 leading-relaxed">{service.desc || t("No description available")}</p>
                        
                        <div className="mb-4">
                          <div className="text-xl font-bold text-blue-600">AUD {service.price}</div>
                          <div className="text-[10px] text-gray-400 font-medium tracking-wider">{t("Service Charge")}</div>
                        </div>
                        
                        <button
                          type="button"
                          onClick={() => setSelectedService(service.id)}
                          className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
                            isSelected 
                            ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {isSelected ? t("Selected") : t("Select")}
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="mt-8 border-t border-gray-200 pt-8">
              <DynamicForm 
                form={form} 
                fields={fields} 
                gridColumns={2} 
                config={{classes: FIELD_CLASSES, showSubmitButton: false, showResetButton: false}} 
              />
            </div>
            
            <Button 
              variant='default'
              type="button"
              onClick={form.handleSubmit(handleSubmit)}
            
              className="w-full mt-6"
            >
              <Wrench className="w-4 h-4 mr-2" /> {t("Submit Service Request")}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Column */}
      <div className="w-full lg:w-[380px] space-y-4">
        <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
          <h3 className="font-bold text-gray-900 mb-4">{t("Request Summary")}</h3>
          
          <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 mb-6">
            <p className="font-semibold text-gray-900 text-sm">{`${accountData?.firstName || ''} ${accountData?.lastName || ''}`.trim()}</p>
            <p className="text-xs text-gray-500">{accountData?.consumerNo}</p>
          </div>

          <div className="flex justify-between items-center text-sm border-b border-gray-100 pb-4">
            <span className="text-gray-500">{t("Service Type")}</span>
            <span className={`font-semibold ${selectedService ? 'text-gray-900' : 'text-gray-400 italic'}`}>
              {getSelectedServiceTitle()}
            </span>
          </div>
        </div>

        <div className="bg-[#f4f7f9] rounded-2xl p-5 border border-gray-200">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-green-600" />
            <h4 className="font-bold text-gray-700 text-sm">{t("Secure & Verified")}</h4>
          </div>
          <ul className="text-xs text-gray-500 space-y-2 font-medium">
            <li>• {t("Account verified via OTP")}</li>
            <li>• {t("Requests tracked with unique ID")}</li>
            <li>• {t("Technician confirmation call provided")}</li>
          </ul>
        </div>
      </div>
    </div>
  );
};
